import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { fetchTierState } from "@/api/tier";

/**
 * The organization's BILLING state — renewal date and scheduled cancellation.
 *
 * This used to be the app's tier source of truth and every feature gate read it. Gates read the
 * entitlement matrix now (`useMyEntitlements` / `useOrgEntitlements`), which is why what is left
 * here is only what `GET /tier` is actually for: when the plan renews, and whether a
 * cancellation is scheduled. The Account > Plan tab is its only real consumer.
 *
 * Deliberately does NOT return the plan. `/tier` is gated on TIER view, so Manager and View Only
 * get a 403 — and returning a "starter" fallback for it is what had those roles seeing a Starter
 * badge on a Pro organization. Which plan an organization is on is an entitlement, readable by
 * any of its members.
 */
export function useTier() {
  const { isSignedIn } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["tier"],
    queryFn: fetchTierState,
    enabled: !!isSignedIn,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  return {
    renewalDate: data?.renewalDate ?? null,
    cancelAtPeriodEnd: data?.cancelAtPeriodEnd ?? false,
    billing: data?.billing ?? null,
    isLoading,
  };
}
